/**
 * Print Configuration
 * Configuration for different printing methods (Print Agent, Bluetooth, etc.)
 */

import { markOfflineCajaDevice } from '../utils/serviceWorker';

export enum PrintMethod {
  PRINT_AGENT = 'agent',    // .NET Print Agent for Windows
  BROWSER = 'browser',      // Browser native printing (fallback)
  BLUETOOTH = 'bluetooth',  // Direct Web Bluetooth API (Chrome/Edge Android)
}

export interface PrinterConfig {
  method: PrintMethod;
  endpoint: string;
  port: number;
  timeout: number; // milliseconds
}

// Print Agent Configuration (Windows PCs)
// Tries port 5001 (production service) first, falls back to 5000 (development)
export const PRINT_AGENT_CONFIG: PrinterConfig = {
  method: PrintMethod.PRINT_AGENT,
  endpoint: 'http://localhost',
  port: 5050, // Production port (Windows Service)
  timeout: 30000, // 30 seconds - increased for printer operations
};

// Development fallback configuration
export const PRINT_AGENT_CONFIG_DEV: PrinterConfig = {
  method: PrintMethod.PRINT_AGENT,
  endpoint: 'http://localhost',
  port: 5000, // Development port
  timeout: 30000,
};

// Bluetooth Configuration (Direct Web Bluetooth API for Android)
export const BLUETOOTH_CONFIG: PrinterConfig = {
  method: PrintMethod.BLUETOOTH,
  endpoint: '', // Not used for Bluetooth
  port: 0, // Not used for Bluetooth
  timeout: 10000, // 10 seconds for connection
};

// Paper specifications for POS-58 thermal printer
export const PAPER_CONFIG = {
  width: 58, // mm
  charactersPerLine: 32, // normal font
  charactersPerLineCondensed: 48, // condensed font
  lineHeight: 24, // dots
};

/**
 * Detect which print method to use based on the platform
 */
export function detectPrintMethod(): PrintMethod {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check if Windows (for future Print Agent)
  const isWindows = userAgent.includes('windows');
  if (isWindows) {
    // For now, use browser printing on Windows
    // Later, we'll check if Print Agent is available
    return PrintMethod.BROWSER;
  }

  // Default fallback
  return PrintMethod.BROWSER;
}

/**
 * Get printer configuration based on detected method
 */
export function getPrinterConfig(method?: PrintMethod): PrinterConfig {
  const printMethod = method || detectPrintMethod();

  switch (printMethod) {
    case PrintMethod.PRINT_AGENT:
      return PRINT_AGENT_CONFIG;
    case PrintMethod.BLUETOOTH:
      return BLUETOOTH_CONFIG;
    case PrintMethod.BROWSER:
    default:
      // Browser printing doesn't need endpoint configuration
      return {
        method: PrintMethod.BROWSER,
        endpoint: '',
        port: 0,
        timeout: 0,
      };
  }
}

/**
 * Check if a print method is available
 */
export async function checkPrintMethodAvailable(method: PrintMethod): Promise<boolean> {
  const config = getPrinterConfig(method);

  if (method === PrintMethod.BROWSER) {
    // Browser printing is always available
    return true;
  }

  // For Print Agent, try production (5050) and development (5000) ports
  if (method === PrintMethod.PRINT_AGENT) {
    const healthPath = '/api/print/health';
    const ports = [5050, 5000]; // Try production first, then development

    for (const port of ports) {
      try {
        const url = `${config.endpoint}:${port}${healthPath}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // Reduced timeout for faster fallback

        const response = await fetch(url, {
          method: 'GET',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          // Update the config port to the working one
          PRINT_AGENT_CONFIG.port = port;
          console.log(`✅ Print Agent available on port ${port}`);
          // Este equipo tiene el agente: es la caja offline (docs/modo-offline.md).
          markOfflineCajaDevice();
          return true;
        }
      } catch (error) {
        // Continue to next port
        console.log(`Port ${port} not available, trying next...`);
      }
    }

    console.warn('Print Agent not available on any port');
    return false;
  }

  // For Bluetooth, check if Web Bluetooth API is available
  if (method === PrintMethod.BLUETOOTH) {
    if (typeof navigator === 'undefined') return false;
    const available = 'bluetooth' in navigator;
    if (available) {
      console.log('✅ Web Bluetooth API available');
    } else {
      console.warn('⚠️ Web Bluetooth API not available');
    }
    return available;
  }

  // For any other method, probe a generic health endpoint
  try {
    const url = `${config.endpoint}:${config.port}/health`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.warn(`Print method ${method} not available:`, error);
    return false;
  }
}
